import { VesselsTrackingModelBase, categoryMap } from './vessels-tracking-model';

import { ProgressModel } from './progress-model';
import { SchedulesService } from '../services/schedules-service';
import { VesselsModel } from './vessels-model';
import { UiStateModel, UiStateModelParam } from './ui-state-model';
import { FilterUtils } from '../../local_modules/voy-global-filters/src/filter-utils';
import { OperatorId } from '../../local_modules/voy-global-filters/src/types';
import { TrackingInfo } from './types/vessels-tracking-model.types';
import { PositionsService } from '../services/positions-service';
import { MdsStatus, VesselCategories, VesselCategory, VesselPositionData, VesselPositionDataExtended } from '../services/types/position-service.types';
import { fixupInvalidPosValues } from '../utils/utils';
import { DbModel } from './db-model';

const CacheTimeoutMinutes = 10;
const TrackingDateToleranceMinutes = 10;

export class VesselsTrackingModelImpl extends VesselsTrackingModelBase {
    loadingPositionsPromise: Promise<void> | null;
    
    constructor() {
        super();

        this.loadingPositionsPromise = this._processVessels();

        // Update positions every CacheTimeoutMinutes
        setInterval(() => { this.loadingPositionsPromise = this._processVessels()}, CacheTimeoutMinutes*60000);
    }

    maerskVesselEmail(imo: number): string | null {
        if (this._maerskVesselPositions.size === 0) {
            return null
        }
        
        const maerskVessel = this._maerskVesselPositions.get(imo)

        if (!maerskVessel || !maerskVessel.mds?.comms) {
            return null
        }

        const email = maerskVessel.mds.comms.find(channel => channel.type === 'EMAIL')?.value ?? null;
        return email
    }

    get allVesselPositions(): Map<number, VesselPositionDataExtended> {
        return this._allVesselPositions;
    }

    // Get the list of filtered vessels based on user input
    get filteredVesselPositions(): Map<number, VesselPositionDataExtended> {
        return this._filteredVesselPositions;
    }

    get imoList() {
        return this._imoList;
    }

    async setIMOList(imoList: number[]) {

        this._imoList = imoList;
        this._setFilteredPositions();
    }

    getTracking(imoNo: number) {
        const tracking = this._tracking.get(imoNo);
        return !tracking ? null : tracking.tracking!;
    }

    async syncTracking(imoNo: number, fromDate: Date, toDate: Date) {
        // TODO: Move this to be handled in IndexedDB
        const previousSyncedTracking = this._tracking.get(imoNo);
        // if 'from date' and 'to date' being fetched are within 10 minutes of what we already have, then no need to do another request
        if (previousSyncedTracking
            && (Math.abs(((fromDate.getTime() - previousSyncedTracking.fromDate.getTime()) / 60000)) <= TrackingDateToleranceMinutes)
            && (Math.abs(((toDate.getTime() - previousSyncedTracking.toDate.getTime()) / 60000)) <= TrackingDateToleranceMinutes)) {
                this.dispatchEvent(new CustomEvent('tracking-updated', {detail: {imoNo}}));
                return;
        }

        let tracking: TrackingInfo = {fromDate, toDate};

        const progressAction = ProgressModel!.actionStarted('vessel-' + imoNo);
        try
        {
            const track = await PositionsService.getVesselTrack(imoNo, fromDate, toDate);

            if (track)
            {
                tracking.tracking = {
                    imoNo,
                    positions: track
                };
            }
        }
        finally
        {
            ProgressModel!.actionCompleted(progressAction);
        }

        if (!tracking.tracking) return;

        this._tracking.set(imoNo, tracking);
        this.dispatchEvent(new CustomEvent('tracking-updated', {detail: {imoNo}}));
    }

    private _tracking: Map<number, TrackingInfo> = new Map();;
    private _imoList: number[] = [];
    private _allVesselPositions: Map<number, VesselPositionDataExtended> = new Map();
    private _maerskVesselPositions: Map<number, VesselPositionDataExtended> = new Map();
    private _nonMaerskVesselPositions: Map<number, VesselPositionDataExtended> = new Map();
    private _filteredVesselPositions: Map<number, VesselPositionDataExtended> = new Map();

    private _setFilteredPositions() {
        const omniFilter = FilterUtils.stringToFilter(UiStateModel?.getValue('q') ?? "");
        const selectedPort = UiStateModel?.getValue(UiStateModelParam.PORT);
        const selectedRegion = UiStateModel?.getValue(UiStateModelParam.PortRegion);
        const showNonMaersk = omniFilter?.commands.find(el => el.vals?.includes(OperatorId.NonMSK.toLowerCase()) || el.cmd?.includes('type') || el.cmd?.includes('ig'));
        this._filteredVesselPositions.clear();

        for (const imo of this._imoList) {
            if (this._maerskVesselPositions.has(imo)) {
                this._filteredVesselPositions.set(imo, {...this._maerskVesselPositions.get(imo)!, isMaersk: true});
            }
            if (this._nonMaerskVesselPositions.has(imo) && (showNonMaersk || selectedRegion || selectedPort)) {
                this._filteredVesselPositions.set(imo, { ...this._nonMaerskVesselPositions.get(imo)!, isMaersk: false });
            }
        }

        this.dispatchEvent(new CustomEvent('filter-positions-updated'));
    }

    async _processVessels() {
        if (this._allVesselPositions.size === 0) {
            const allVessels = await DbModel!.getAllPositionsRecords();
     
            if (allVessels.length > 0) {
                allVessels.forEach(v => {
                    if (v.isMaersk) {
                        this._maerskVesselPositions.set(v.imo, v);
                    } else {
                        this._nonMaerskVesselPositions.set(v.imo, v)
                    }
                    this._allVesselPositions.set(v.imo, v);
                })
                this.dispatchEvent(new CustomEvent('positions-updated'));
            } 

            this._getLatestPositions();
        } else {
            this._getLatestPositions();
        }
    }
    private async _getLatestPositions() {
        const response = await PositionsService.getLatest(); // position api /latest
        if (!response) return;
        response.forEach((p: VesselPositionData) => {
            const isMaerskVessel = p.hasOwnProperty("mds") && p.mds!.basic?.status !== MdsStatus.OUTFLEETED;
            fixupInvalidPosValues(p);
            if (isMaerskVessel) {
                this._allVesselPositions.set(p.imo, { ...p, isMaersk: true, vesselCategory: this._getVesselCategory(true, p.vesselType), vesselClass: "" });
                this._maerskVesselPositions.set(p.imo, { ...p, isMaersk: true, vesselCategory: this._getVesselCategory(true, p.vesselType), vesselClass: "" });
            } else {
                this._allVesselPositions.set(p.imo, { ...p, isMaersk: false, vesselCategory: this._getVesselCategory(false, p.vesselType), vesselClass: "" });
                this._nonMaerskVesselPositions.set(p.imo, { ...p, isMaersk: false, vesselCategory: this._getVesselCategory(false, p.vesselType), vesselClass: "" });
            }
        });
        await DbModel!.populatePositionsTable(this._allVesselPositions)
        VesselsModel?.refreshVessels();
        if(!sessionStorage.getItem("positions_updated_at")){
            sessionStorage.setItem("positions_updated_at", JSON.stringify(new Date()));
        }
        this.dispatchEvent(new CustomEvent('positions-updated'));
    }

    private _getVesselCategory(isMaersk: boolean, vesselType: string): VesselCategory {
        if (isMaersk) {
            return VesselCategories.ContainerCargo;
        }
        
        return categoryMap.get(vesselType) || VesselCategories.Other;
    }

    public centerMapOnVessel(){
        this.dispatchEvent(new Event('center-on-map'));
    }
}
